/**
 * @fileoverview Shell for signed-in (dashboard-area) pages.
 * @description Renders the navy sidebar (brand mark, privilege-filtered nav) alongside the
 * shared DashboardHeader and the current route's content. Every page reachable
 * after login sits inside this shell via the router. The outer shell is capped to exactly the
 * viewport height (`h-screen overflow-hidden`) rather than `min-h-screen`, so the page itself
 * never scrolls — only `<main>` does (`flex-1 overflow-y-auto`). With `min-h-screen`, the shell
 * grows taller than the viewport once a page's content is tall, and the browser scrolls the whole
 * page instead, dragging the sidebar and header out of view along with it; capping the shell's
 * height is what makes `main`'s own `overflow-y-auto` actually the thing that scrolls. The nav
 * list also gets its own `overflow-y-auto` so a short viewport with many nav items scrolls just
 * that list rather than breaking the fixed shell. Logout lives in DashboardHeader's user-menu
 * dropdown now, not here, so the sidebar has no reason to reserve its own scroll region to stay
 * reachable.
 *
 * Below the `lg` breakpoint the sidebar becomes a collapsible off-canvas drawer (fixed, translated
 * off-screen, toggled via DashboardHeader's hamburger button) with a dismissible backdrop, rather
 * than permanently occupying a fixed 232–296px column on a phone-width viewport; at `lg` and up it's
 * back to a normal, always-visible flex column (`lg:static lg:translate-x-0`). The drawer also
 * closes on the backdrop click and via NavMenu's onNavigate (fired by the nav link click itself,
 * not a useLocation effect — an unconditional setState in an effect trips this repo's
 * set-state-in-effect lint rule, and closing as part of the click gesture is the more direct fix
 * anyway). Takes `privileges` and `notifications` as props rather than resolving them itself, since
 * `shared/` may not import a feature (ADR-0002) — DashboardShell (the app/ layer) resolves both
 * before this shell ever renders and passes them down.
 *
 * Separately, at `lg` and up, the sidebar can also collapse to a narrow icon-only rail via its own
 * bottom toggle button — a desktop power-user affordance, distinct from the mobile off-canvas
 * drawer above (which always shows full labels regardless of this preference, since a touch user
 * never benefits from icon-only rows). The collapsed width only ever applies at `lg:` — the base
 * (mobile) width is untouched — and the preference is remembered in `localStorage` across reloads.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'

import logoUrl from '@/assets/images/login/logo.png'
import DashboardHeader from '@/shared/components/molecules/DashboardHeader'
import NavMenu from '@/shared/components/molecules/NavMenu'
import SidebarCollapseToggle from '@/shared/components/molecules/SidebarCollapseToggle'
import { useAuth } from '@/shared/lib/auth/useAuth'

import type {
  DashboardHeaderBank,
  DashboardHeaderNotificationsData,
} from '@/shared/components/molecules/DashboardHeader'

interface DashboardLayoutProps {
  privileges: string[]
  notifications: DashboardHeaderNotificationsData
  bank?: DashboardHeaderBank
  onOpenProfile?: () => void
  onOpenOrganisation?: () => void
}

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'brownbox.layout.sidebarCollapsed'

/**
 * @description Reads the desktop sidebar-collapse preference persisted from a previous visit.
 * @returns {boolean} `true` if the sidebar was left collapsed last time.
 */
function readStoredCollapsed(): boolean {
  return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1'
}

/**
 * @component
 * @param {DashboardLayoutProps} props - The resolved privileges and notification data for the nav
 * and header.
 * @param {string[]} props.privileges - Privilege keys from GET /identity/me, passed through to
 * NavMenu.
 * @param {DashboardHeaderNotificationsData} props.notifications - The notification list, unread
 * count, and mark-read callbacks, passed through to DashboardHeader.
 * @param {DashboardHeaderBank} [props.bank] - The signed-in Banking-role user's bank identity;
 * passed through to DashboardHeader only — the sidebar's own brand mark is always the InsightBox
 * logo, regardless of module.
 * @param {() => void} [props.onOpenProfile] - Opens the profile dialog; passed through to
 * DashboardHeader's user menu.
 * @param {() => void} [props.onOpenOrganisation] - Opens the organisation details dialog; passed
 * through to DashboardHeader's user menu, which shows its item only when this is given.
 */
function DashboardLayout({
  privileges,
  notifications,
  bank,
  onOpenProfile,
  onOpenOrganisation,
}: DashboardLayoutProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(readStoredCollapsed)
  // Sales nav labels are all short ("Sales Performance" is the longest), so the sidebar runs
  // narrower there; Banking keeps the wider column its long labels ("Overdue & NPA (Asset
  // Quality)", "Membership & Share Capital") need to avoid truncating.
  const isBankingModule = privileges.some((privilege) => privilege.startsWith('banking:'))
  const expandedWidth = isBankingModule ? 'w-[296px]' : 'w-[232px]'
  const expandedLgWidth = isBankingModule ? 'lg:w-[296px]' : 'lg:w-[232px]'

  /** @description Toggles the sidebar's full/icon-only width, persisting the choice. */
  function toggleCollapsed() {
    setCollapsed((value) => {
      const next = !value
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0')
      return next
    })
  }

  return (
    <div className="flex h-screen overflow-hidden bg-input-bg">
      {mobileNavOpen && (
        <div
          aria-hidden="true"
          onClick={() => setMobileNavOpen(false)}
          className="fixed inset-0 z-30 bg-ink/40 lg:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex ${expandedWidth} shrink-0 flex-col bg-accent transition-[transform,width] duration-200 ease-in-out lg:static lg:z-auto lg:translate-x-0 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'lg:w-[72px]' : expandedLgWidth}`}
      >
        {/* Always the InsightBox logo, centered — the same brand mark for every module (the bank
            identity lives in the header instead, not here). The collapsed rail (72px) is too
            narrow for the full wordmark image, so it falls back to a compact "IB" there.
            `brightness-0 invert` turns the logo into a solid white silhouette — the source PNG's
            colors (blue "Insight", navy "Box", black tagline) were made for a white background
            (that's how it renders in the header), so on this navy sidebar the navy/black parts
            would otherwise blend straight into the background. */}
        <div className="flex h-[66px] shrink-0 items-center justify-center border-b border-white/10 px-4">
          <img
            src={logoUrl}
            alt="InsightBox"
            className={`h-7 w-auto max-w-full object-contain brightness-0 invert ${collapsed ? 'lg:hidden' : ''}`}
          />
          {collapsed && (
            <p className="hidden text-[20px] font-bold tracking-[-0.3px] text-white lg:block">
              I<span className="font-normal text-accent-text">B</span>
            </p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-2.5 py-3">
          <NavMenu
            privileges={privileges}
            onNavigate={() => setMobileNavOpen(false)}
            collapsed={collapsed}
          />
        </div>
        {/* Icon-only toggle + version share one footer row; version hides once collapsed. */}
        <div
          className={`flex shrink-0 items-center justify-between gap-2 border-t border-white/10 p-2.5 ${collapsed ? 'lg:justify-center' : ''}`}
        >
          <div className="hidden lg:block">
            <SidebarCollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} />
          </div>
          <p
            className={`truncate text-[10.5px] text-accent-text/60 ${collapsed ? 'lg:hidden' : ''}`}
          >
            {t('layout.version', { version: __APP_VERSION__ })}
          </p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <DashboardHeader
          user={user}
          privileges={privileges}
          notifications={notifications}
          onMenuClick={() => setMobileNavOpen((open) => !open)}
          {...(bank && { bank })}
          {...(onOpenProfile && { onOpenProfile })}
          {...(onOpenOrganisation && { onOpenOrganisation })}
        />
        <main className="flex flex-1 flex-col overflow-y-auto p-4 sm:p-6">
          {/* `grow` stretches this div to fill any leftover space when the routed page is
              shorter than the viewport, pushing the footer below down to the very bottom
              instead of floating right under the content. `shrink-0` is the important half —
              without it, `grow`'s default flex-basis (0%) lets the flexbox algorithm compute a
              box height SMALLER than the page's actual content on a tall page; with the box's
              default `overflow: visible`, that content still paints past the box's own edge
              (it isn't clipped), but the footer — laid out immediately after using that
              too-small computed height — ends up overlapping it instead of sitting below it.
              `shrink-0` keeps this div's layout height pinned to its content's real size, so it
              can only ever grow past that, never shrink under it; `main`'s own overflow-y-auto
              is what scrolls when that combined height exceeds the viewport. It's also a flex
              column, so a page that wants to fill that leftover space too (e.g. User
              Management's table card) can opt in with its own `grow`; other pages keep their
              natural height. */}
          <div className="flex grow shrink-0 flex-col">
            <Outlet />
          </div>
          <p className="flex shrink-0 justify-end pt-2.5 text-[11.5px] text-placeholder">
            {t('layout.poweredBy')}
          </p>
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
